import {
  Backdrop,
  Box,
  Button,
  Fade,
  Grid,
  Modal,
  Tooltip,
  Typography,
} from '@mui/material';
import * as React from 'react';
import dayjs from 'dayjs';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { GENERAL_FONT, MHColours } from '../../constants/theme';
import { IDateRangeStatistics } from '../../lib/interfaces';
import { calculateAverageHeadache } from '../../lib/helpers/diary';
import {
  exportButtonStyle,
  exportButtonStyleDisabled,
} from '../PatientDetails/PatientDetails';
import { reportError } from '../../lib/sentry';

const compareYearMonthDesc = (a: string, b: string) => {
  const [ay, am] = a.split('-').map(Number);
  const [by, bm] = b.split('-').map(Number);
  return by - ay || bm - am; // year desc, then month desc
};

export interface PeriodOverview {
  statistics?: IDateRangeStatistics;
  statisticsByMonths?: Record<string, IDateRangeStatistics>;
  overviewButtonDisabled: boolean;
}

const Dot = styled.div<{ color: string }>`
  height: 15px;
  width: 15px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex: 0 0 auto;
`;
const LabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
`;

export const EventContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px 0 0;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
  justify-content: flex-start;
  overflow: auto;
  max-height: 70vh;
`;
const AverageValue = styled.div`
  font-family: ${GENERAL_FONT};
  margin: 0 20px 0 12px;
`;

const Average = styled.span`
  font-family: ${GENERAL_FONT};
  margin-right: 10px;
`;

const AverageDiv = styled.span`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  font-family: ${GENERAL_FONT};
  font-size: 16px;
`;

const MonthHeader = styled.div`
  font-family: ${GENERAL_FONT};
  font-size: 18px;
  font-weight: bold;
  margin: 30px 0 5px 0;
  text-align: left;
`;

const TimePeriodOverview = (props: PeriodOverview) => {
  const { statistics, statisticsByMonths, overviewButtonDisabled } = props;
  const [modalOpen, setModalOpen] = React.useState(false);
  const [tooltioOpen, setTooltipOpen] = React.useState(false);
  const [copyFeedback, setCopyFeedback] = React.useState('');
  const { t } = useTranslation();

  if (!statistics) return null;

  const handleClose = () => {
    setTooltipOpen(false);
  };

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleCopy = () => {
    const element = document.getElementById('modalContentId');
    if (!element) return;

    let extractedText = '';

    element.querySelectorAll('.month-section').forEach((monthSection) => {
      const monthText =
        monthSection.querySelector('.month-header')?.textContent?.trim() || '';
      extractedText += `\n${monthText}\n`;

      monthSection.querySelectorAll('.event-row').forEach((event) => {
        const label = event.querySelector('.event-label')?.textContent?.trim();
        const value = event.querySelector('.event-value')?.textContent?.trim();
        if (label && value) {
          extractedText += `${label}: ${value}\n`;
        }
      });

      monthSection.querySelectorAll('.average-row').forEach((avg) => {
        extractedText += `${avg.textContent?.trim()}\n`;
      });
    });

    extractedText = extractedText.trim();
    navigator.clipboard
      .writeText(extractedText)
      .then(() => {
        setCopyFeedback(t('copiedSuccessfully'));
        setTooltipOpen(true);
      })
      .catch((err) =>
        reportError(err, { level: 'warning', tags: { area: 'clipboard' } })
      );
  };

  const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 450,
    bgcolor: 'background.paper',
    border: 'none',
    boxShadow: 24,
    p: 4,
    borderRadius: '5px',
  };

  const showStatistics = (
    statObj: IDateRangeStatistics,
    columnWidth: 6 | 12
  ) => {
    if (!statObj) {
      return null;
    }

    const renderStatistic = (
      value: number | undefined,
      label: string,
      color: string
    ) => {
      if (value === undefined || value === 0) return null;

      return (
        <EventContainer className="event-row" key={label}>
          <LabelGroup>
            <Dot color={color} />
            <span className="event-label">{t(label)}</span>
          </LabelGroup>
          <AverageValue className="event-value">{value ?? 0}</AverageValue>
        </EventContainer>
      );
    };

    const statisticsList: Array<{
      key: keyof IDateRangeStatistics;
      label: string;
      color: string;
    }> = [
      {
        key: 'numberOfDays',
        label: 'numberOfDaysAnswered',
        color: MHColours.mhGrayBorder,
      },
      {
        key: 'numberOfDaysWithHeadaches',
        label: 'headache',
        color: MHColours.mhMigraineRed,
      },
      {
        key: 'numberOfMigraines',
        label: 'migrain',
        color: MHColours.mhMigraineBlue,
      },
      {
        key: 'numberOfTensionHeadaches',
        label: 'tensionHeadache',
        color: MHColours.mhMigrainePurpleLight,
      },
      {
        key: 'numberOfHortons',
        label: 'horton',
        color: MHColours.mhMigraineYellow,
      },
      {
        key: 'numberOfOtherHeadaches',
        label: 'otherHeadache',
        color: MHColours.mhMigraineOrange,
      },
      { key: 'numberOfAuras', label: 'aura', color: MHColours.mhMigrainePink },
      {
        key: 'numberOfAurasWithoutHeadaches',
        label: 'auraWithoutHeadache',
        color: MHColours.mhMigrainePurple,
      },
      {
        key: 'numberOfWithoutHeadaches',
        label: 'noHeadache',
        color: MHColours.mhMigraineGreenLight,
      },
      {
        key: 'numberOfLakemedels',
        label: 'medication',
        color: MHColours.mhMigraineGreenFluorescent,
      },
      {
        key: 'numberOfMens',
        label: 'menstruation',
        color: MHColours.mhMigraineGreenDark,
      },
    ];

    const averageConfig: Array<{
      label: string;
      sumKey: keyof IDateRangeStatistics;
      countKey: keyof IDateRangeStatistics;
    }> = [
      {
        label: 'vasMigraine',
        sumKey: 'totalSumOfIntensitiesMigraines',
        countKey: 'numberOfMigraines',
      },
      {
        label: 'vasTensionHeadache',
        sumKey: 'totalSumOfIntensitiesTensionHeadaches',
        countKey: 'numberOfTensionHeadaches',
      },
      {
        label: 'vasHorton',
        sumKey: 'totalSumOfIntensitiesHortons',
        countKey: 'numberOfHortons',
      },
      {
        label: 'vasOtherHeadache',
        sumKey: 'totalSumOfIntensitiesOtherHeadaches',
        countKey: 'numberOfOtherHeadaches',
      },
    ];

    return (
      <Grid
        style={{ textAlign: 'left' }}
        container
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
      >
        <Grid item xs={columnWidth} className="statistics-column">
          {statisticsList.map(({ key, label, color }) =>
            renderStatistic(statObj[key], label, color)
          )}
          {columnWidth === 6 && (
            <Button
              variant="contained"
              sx={{ textTransform: 'none' }}
              style={
                Object.keys(statisticsByMonths ?? {}).length === 0 ||
                overviewButtonDisabled
                  ? exportButtonStyleDisabled
                  : exportButtonStyle
              }
              onClick={handleModalOpen}
              disabled={
                Object.keys(statisticsByMonths ?? {}).length === 0 ||
                overviewButtonDisabled
              }
            >
              {t('summary')}
            </Button>
          )}
        </Grid>

        <Grid
          item
          xs={columnWidth}
          style={{ marginTop: '10px' }}
          className="averages-column"
        >
          {averageConfig.map(({ label, sumKey, countKey }) => {
            const count = statObj[countKey] || 0;
            const avg = calculateAverageHeadache(statObj[sumKey] || 0, count);
            return count > 0 ? (
              <AverageDiv key={label} className="average-row">
                <Average>
                  {t(label)}&nbsp;{t('average')}: {avg}
                </Average>
              </AverageDiv>
            ) : null;
          })}
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      {showStatistics(statistics, 6)}
      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={modalOpen}>
          <Box sx={modalStyle}>
            <Typography
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
                fontFamily: GENERAL_FONT,
              }}
            >
              {t('summary')}
            </Typography>
            <ModalContent id="modalContentId">
              {Object.keys(statisticsByMonths ?? {})
                .sort(compareYearMonthDesc)
                .map((key) => {
                  const diaryDate = dayjs(key);
                  const formattedDate = diaryDate.format('MMMM YYYY');
                  const capitalizedDate =
                    formattedDate.charAt(0).toUpperCase() +
                    formattedDate.slice(1);
                  return (
                    <div key={key} className="month-section">
                      <MonthHeader className="month-header">
                        {capitalizedDate}
                      </MonthHeader>
                      <div className="month-statistics">
                        {showStatistics(statisticsByMonths![key], 12)}
                      </div>
                    </div>
                  );
                })}
            </ModalContent>
            <Tooltip
              placement="top"
              open={tooltioOpen}
              onClose={handleClose}
              title={copyFeedback}
              leaveDelay={500}
            >
              <Button
                variant="contained"
                style={{
                  ...exportButtonStyle,
                  float: 'right',
                  marginTop: '36px',
                  marginBottom: '0px',
                  marginLeft: 'auto',
                }}
                sx={{ textTransform: 'none' }}
                onClick={handleCopy}
              >
                {t('copy')}
              </Button>
            </Tooltip>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default TimePeriodOverview;
